import { Button, Flex, Paper, Text, rem, Loader, Stack, Group, ThemeIcon, Badge, Divider } from "@mantine/core"; // replace 'your-ui-library' with the actual library you're using
import { IconCalendar, IconCheck, IconMapPin, IconWheelchair, IconCar } from "@tabler/icons-react";
import { memo, useState } from "react";
import { formattedDateTime, humenReadableTime } from "../../utils/datetime";
import { JobInfoProps } from "../CreateJob/type";
import { modals } from "@mantine/modals";
import axios from "axios";
import { API_URL } from "../../services/url";
import { showNotification } from "@mantine/notifications";
import { AcceptOrDeclineJob } from "../JobInfo/acceptordeclinejob";

const style = { width: rem(16), height: rem(16) };

const LocationIcon = <IconMapPin style={style} />;

const CalendarIcon = <IconCalendar style={style} />;

const CarIcon = <IconCar style={style} />;

const WheelChairIcon = <IconWheelchair style={style} color="blue" />;

const AllJobs = memo(
  ({
    job,
    driverId,
    requestlist,
    reFresh,
  }: {
    job: JobInfoProps;
    driverId: string | null;
    requestlist: string[] | undefined;
    reFresh: () => void
  }) => {
    // replace 'your-driver-id' with the actual driver id

    const [loading, setLoading] = useState(false);

    const userId = localStorage.getItem("userId");

    const isUserPost = job.posted_by_mail !== null && job.number_of_guest !== null;

    // requesting job
    const alertModal = (e: any) => {
      e.stopPropagation();
      modals.openConfirmModal({
        title: "Request Job",
        centered: true,
        children: (
          <Text size="sm">{"Are you sure want to request this job?"}</Text>
        ),
        labels: { confirm: "Request Job", cancel: "No don't request it" },
        confirmProps: { color: "primary" },
        onCancel: () => console.log("Cancel"),
        onConfirm: () => {
          isUserPost ? handleAcceptJob() : handleRequestJob();
        },
      });
    };

    const handleRequestJob = async () => {
      try {
        setLoading(true);
        const res = await axios.post(`${API_URL}job-request/`, {
          status: "pending",
          job: job?.id,
          requested_by_driver: userId,
        });
        if (res.data && res.data.id) {
          showNotification({
            title: "Success",
            message: "Job requested successfully",
            color: "green",
          });
          requestlist?.push(job.id);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    const handleAcceptJob = async () => {
      try {
        setLoading(true);
        const response = await axios.post(`${API_URL}assignjob-driverid/`, {
          job_id: job.id,
          driver_id: driverId
        });
        if (response.data.message.includes('successfully')) {
          showNotification({
            title: "Success",
            message: "Job requested successfully",
            color: "green",
          });
          reFresh()
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.log("🚀 ~ handleReAssign ~ error:", error);
      }
    }

    return (
      <Paper
        shadow="sm"
        p="lg"
        radius="md"
        withBorder
        mt="md"
        key={job.id}
        pos={"relative"}
        onClick={() => (window.location.href = `/job?id=${job.id}`)}
        style={{ cursor: "pointer" }}
      >
        <Flex pos={'absolute'} top={'45%'} left={'45%'}>
          {loading && <Loader color="blue" />}
        </Flex>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon variant="light" color="gray">
                <IconCalendar style={{ width: rem(16), height: rem(16) }} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {formattedDateTime(job.pickup_at)}
              </Text>
            </Group>
            <Badge color="dark" size="xl" variant="filled">
              {job.booking_id}
            </Badge>
          </Group>

          <Divider />

          <Stack gap="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="gray" size="sm">
                <IconMapPin style={{ width: rem(14), height: rem(14) }} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {job.pickupLoc}
              </Text>
              <Text size="xs" c="dimmed">
                (Pickup)
              </Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon variant="light" color="gray" size="sm">
                <IconMapPin style={{ width: rem(14), height: rem(14) }} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {job.dropLoc}
              </Text>
              <Text size="xs" c="dimmed">
                (Drop)
              </Text>
            </Group>
          </Stack>

          <Divider />

          <Group justify="space-between">
            <Group gap="xs" align="center">
              <ThemeIcon
                variant="light"
                color={job.services_required.includes("wheel") ? "blue" : "gray"}
              >
                {job.services_required.includes("wheel") ? (
                  <IconWheelchair style={{ width: rem(16), height: rem(16) }} />
                ) : (
                  <IconCar style={{ width: rem(16), height: rem(16) }} />
                )}
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {job.services_required}
              </Text>
            </Group>
            {job.repeat_booking && (
              <Badge variant="light" color="teal">
                Regular Ride
              </Badge>
            )}
          </Group>

          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">
              Posted {humenReadableTime(job.posted_at)}
            </Text>
            <Text size="lg" fw={700}>
              {job.amount ? `$${(job.amount).toFixed(2)}` : ""}
            </Text>
          </Group>

          {job.posted_by !== userId ? (
            requestlist && requestlist.length > 0 && requestlist.includes(job.id) ? (
              <Button
                radius="md"
                size="sm"
                color="gray"
                leftSection={<IconCheck size={16} />}
                variant="outline"
                disabled={true}
                fullWidth
              >
                Requested
              </Button>
            ) : (
              <Button
                radius="md"
                size="md"
                variant="gradient"
                gradient={{ from: "dark", to: "gray", deg: 145 }}
                disabled={loading}
                onClick={(e) => alertModal(e)}
                loading={loading}
                loaderProps={{ type: "oval" }}
                fullWidth
              >
                Request Job
              </Button>
            )
          ) : (
            <Button
              radius="md"
              size="sm"
              color="gray"
              variant="outline"
              disabled={true}
              fullWidth
            >
              Posted by you
            </Button>
          )}
        </Stack>
      </Paper>
    );
  }
);

export default AllJobs;
